import React, { useEffect, useRef, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor, Essentials, Paragraph, Heading,
  Bold, Italic, Underline, Strikethrough,
  Alignment, FontFamily, FontSize, FontColor, FontBackgroundColor,
  Link, List,
  Image, ImageToolbar, ImageCaption, ImageStyle, ImageResize, ImageUpload, FileRepository,
  Table, TableToolbar, BlockQuote, HorizontalLine,
  type EditorConfig
} from "ckeditor5";
import ckImageUploadPlugin from "@/kb/helpers/ckImageUploadPlugin";
import { GLOBAL_VARIABLES } from "./triggerTypes";
import useApi from "@/hooks/useApi";
import GrapesEmailEditor from "./GrapesEmailEditor";


interface Props {
  template: {
    id?: number;
    name: string;
    category: string;
    subject: string;
    bodyHtml: string;
    bodyText: string;
    fromName: string;
    fromAddress: string;
  };
  onChange: (field: string, value: string) => void;
}

function buildEditorConfig(uploadUrl: string): EditorConfig {
  return {
    licenseKey: "GPL",
    plugins: [
      Essentials, Paragraph, Heading,
      Bold, Italic, Underline, Strikethrough,
      Alignment, FontFamily, FontSize, FontColor, FontBackgroundColor,
      Link, List,
      Image, ImageToolbar, ImageCaption, ImageStyle, ImageResize, ImageUpload, FileRepository,
      Table, TableToolbar, BlockQuote, HorizontalLine
    ],
    toolbar: [
      "heading", "|",
      "bold", "italic", "underline", "strikethrough", "|",
      "alignment", "fontFamily", "fontSize", "fontColor", "fontBackgroundColor", "|",
      "link", "bulletedList", "numberedList", "|",
      "imageUpload", "insertTable", "blockQuote", "horizontalLine", "|",
      "undo", "redo"
    ],
    extraPlugins: [ckImageUploadPlugin(uploadUrl)]
  };
}

export default function TemplateEditor({ template, onChange }: Props) {
  const api = useApi();
  const [activeTab, setActiveTab] = useState<"design" | "visual" | "html" | "text" | "preview">("design");
  const [aiSubjects, setAiSubjects] = useState<string[]>([]);
  const [aiLoading, setAiLoading] = useState(false);

  const generateSubjectLines = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiSubjects([]);
    try {
      const bodyText = template.bodyHtml?.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").slice(0, 800).trim();
      const prompt = `You are an expert email marketer. Generate 5 compelling, high-converting subject lines for this email campaign.\n\nEmail body excerpt:\n${bodyText || "(no body yet)"}\n\nReturn ONLY a JSON array of 5 subject line strings. No explanation, no markdown fences, no extra text — just the JSON array.`;
      const res = await api.admin.post("Copilot/Chat", {
        messages: [{ role: "user", content: prompt }],
        context: "You are helping generate email subject lines. Respond only with a JSON array.",
      });
      const data = await res.json();
      const text: string = data?.reply ?? data?.message ?? "";
      const match = text.match(/\[[\s\S]*?\]/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        if (Array.isArray(parsed)) setAiSubjects(parsed.map((s: any) => String(s)).slice(0, 5));
      }
    } catch {
      setAiSubjects(["Could not generate suggestions. Try again."]);
    } finally {
      setAiLoading(false);
    }
  };

  const editorRef = useRef<any>(null);
  const [verifiedDomains, setVerifiedDomains] = useState<string[]>([]);

  // Load verified sender domains for the From address picker
  useEffect(() => {
    api.admin.get("SenderDomains").then(r => r.json()).then(d => {
      if (Array.isArray(d)) {
        setVerifiedDomains(d.filter((x: any) => x.status === "Verified").map((x: any) => x.domainName));
      }
    }).catch(() => {});
  }, []);

  // Split fromAddress into local-part and domain for the picker
  const atIdx = (template.fromAddress ?? "").lastIndexOf("@");
  const fromLocal  = atIdx >= 0 ? template.fromAddress.slice(0, atIdx) : (template.fromAddress ?? "");
  const fromDomain = atIdx >= 0 ? template.fromAddress.slice(atIdx + 1) : (verifiedDomains[0] ?? "");

  const handleFromLocalChange = (local: string) => {
    onChange("fromAddress", fromDomain ? `${local}@${fromDomain}` : local);
  };
  const handleFromDomainChange = (domain: string) => {
    onChange("fromAddress", fromLocal ? `${fromLocal}@${domain}` : `@${domain}`);
  };

  const insertVariable = (token: string) => {
    if (activeTab === "visual" && editorRef.current) {
      editorRef.current.model.change((writer: any) => {
        const insertPosition = editorRef.current.model.document.selection.getFirstPosition();
        writer.insertText(token, insertPosition);
      });
    } else {
      onChange("bodyHtml", (template.bodyHtml ?? "") + token);
    }
  };

  return (
    <div className="space-y-4">
      {/* From / Subject row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From Name</label>
          <input
            className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm"
            placeholder="e.g. Acme Support"
            value={template.fromName ?? ""}
            onChange={e => onChange("fromName", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From Address</label>
          {verifiedDomains.length === 0 ? (
            <div className="flex items-center gap-1.5 border border-amber-200 bg-amber-50 rounded px-3 py-1.5">
              <svg className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span className="text-xs text-amber-700">No verified domains yet — add one in <strong>Settings → Sending Domains</strong></span>
            </div>
          ) : (
            <div className="flex items-center border border-gray-200 rounded overflow-hidden text-sm">
              <input
                className="flex-1 min-w-0 px-3 py-1.5 border-none outline-none"
                placeholder="hello"
                value={fromLocal}
                onChange={e => handleFromLocalChange(e.target.value)}
              />
              <span className="px-1 text-gray-400 select-none">@</span>
              <select
                className="border-none outline-none bg-gray-50 px-2 py-1.5 text-sm text-gray-700 border-l border-gray-200"
                value={fromDomain}
                onChange={e => handleFromDomainChange(e.target.value)}
              >
                {verifiedDomains.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-500">Subject Line</label>
          <button
            type="button"
            onClick={generateSubjectLines}
            disabled={aiLoading}
            className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50"
          >
            {aiLoading ? (
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.416a2 2 0 01-1.487.657H9.52a2 2 0 01-1.487-.657l-.347-.416z" />
              </svg>
            )}
            {aiLoading ? "Generating…" : "AI Suggestions"}
          </button>
        </div>
        <input
          className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm"
          placeholder="e.g. Thanks for chatting with us, <<contact.firstName>>"
          value={template.subject ?? ""}
          onChange={e => onChange("subject", e.target.value)}
        />
        {aiSubjects.length > 0 && (
          <div className="mt-2 border border-purple-100 rounded-lg bg-purple-50 overflow-hidden">
            <p className="text-[10px] text-purple-500 px-3 pt-2 pb-1 font-medium uppercase tracking-wide">Click to use</p>
            {aiSubjects.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { onChange("subject", s); setAiSubjects([]); }}
                className="w-full text-left px-3 py-1.5 text-xs text-gray-800 hover:bg-purple-100 border-t border-purple-100 first:border-t-0"
              >
                {s}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setAiSubjects([])}
              className="w-full text-center py-1.5 text-[10px] text-gray-400 hover:text-gray-600 border-t border-purple-100"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Variable picker */}
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1.5">Insert Variable</p>
        <div className="flex flex-wrap gap-1.5">
          {GLOBAL_VARIABLES.map(v => (
            <button
              key={v.token}
              type="button"
              title={v.label}
              onClick={() => insertVariable(v.token)}
              className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs hover:bg-blue-100 font-mono"
            >
              {v.token}
            </button>
          ))}
          <button
            type="button"
            onClick={() => insertVariable("{{unsubscribe_url}}")}
            className="px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded text-xs hover:bg-orange-100 font-mono"
            title="Unsubscribe link (auto-included in footer)"
          >
            {"{{unsubscribe_url}}"}
          </button>
        </div>
      </div>

      {/* Body editor tabs */}
      <div>
        <div className="flex border-b border-gray-200 mb-2 overflow-x-auto">
          {([
            { id: "design",  label: "✦ Design Builder", tip: "Drag-and-drop email builder — recommended" },
            { id: "visual",  label: "Rich Text",         tip: "Simple formatting with CKEditor" },
            { id: "html",    label: "HTML",              tip: "Raw HTML source" },
            { id: "text",    label: "Plain Text",        tip: "Fallback for clients that block HTML" },
            { id: "preview", label: "Preview",           tip: "Rendered preview" },
          ] as const).map(tab => (
            <button
              key={tab.id}
              type="button"
              title={tab.tip}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-1.5 text-sm border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600 font-medium"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "design" && (
          <GrapesEmailEditor
            value={template.bodyHtml ?? ""}
            onChange={html => onChange("bodyHtml", html)}
            uploadUrl={`${import.meta.env.VITE_ADMIN_API_URL}/api/admin/CampaignAssets/upload`}
          />
        )}

        {activeTab === "visual" && (
          <div className="border border-gray-200 rounded overflow-hidden min-h-[260px]">
            <CKEditor
              editor={ClassicEditor}
              config={buildEditorConfig(`${import.meta.env.VITE_ADMIN_API_URL}/api/admin/CampaignAssets/upload`)}
              data={template.bodyHtml ?? ""}
              onReady={(editor: any) => { editorRef.current = editor; }}
              onChange={(_: any, editor: any) => onChange("bodyHtml", editor.getData())}
            />
          </div>
        )}

        {activeTab === "html" && (
          <textarea
            className="w-full border border-gray-200 rounded px-3 py-2 text-sm font-mono h-64 resize-y"
            placeholder="<p>Hi <<contact.firstName>>,</p><p>Thanks for chatting with us...</p>"
            value={template.bodyHtml ?? ""}
            onChange={e => onChange("bodyHtml", e.target.value)}
          />
        )}

        {activeTab === "text" && (
          <textarea
            className="w-full border border-gray-200 rounded px-3 py-2 text-sm font-mono h-64 resize-y"
            placeholder={"Hi <<contact.firstName>>,\n\nThanks for chatting with us..."}
            value={template.bodyText ?? ""}
            onChange={e => onChange("bodyText", e.target.value)}
          />
        )}

        {activeTab === "preview" && (
          <div className="border border-gray-200 rounded overflow-hidden">
            {/* Mobile/desktop toggle */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
              <span className="font-medium">Preview</span>
              <span className="text-gray-300">|</span>
              <span>Subject: <span className="text-gray-800">{template.subject || "(no subject)"}</span></span>
            </div>
            <div
              className="p-6 bg-white min-h-[260px] text-sm overflow-auto"
              style={{ maxWidth: 600, margin: "0 auto" }}
              dangerouslySetInnerHTML={{ __html: template.bodyHtml ?? "<em style='color:#999'>Nothing to preview yet.</em>" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
